/**
 * Members and invites (firm-model spec §5.3, §5.4, §10.2 Members page). Declared once here;
 * the server returns them and the web panel parses them.
 */
import { z } from "zod";
import { expectParity } from "../parity.js";
import type { FirmRole } from "./me.js";

const role = z.enum(["admin", "member"]);

export type FirmMember = {
  userId: string;
  email: string;
  name: string | null;
  role: FirmRole;
  status: "active" | "disabled";
  /** Day granularity (stamped by /v1/me); null = never since A1b. */
  lastSeenAt: string | null;
  joinedAt: string;
  /** Credits this member used this period (from `usage_events.user_id`, spec §9). Null
   * from a server older than A6. */
  creditsUsed: number | null;
};

export type FirmInvite = {
  id: string;
  email: string;
  role: FirmRole;
  invitedAt: string;
  expiresAt: string;
  expired: boolean;
};

export type MembersResponse = {
  members: FirmMember[];
  invites: FirmInvite[];
  /** Why this firm can't invite right now (not on Team, or every seat in use), or null. */
  inviteBlockedReason: string | null;
  /** Invites that can still go out before the seats run out; null = no seat limit (beta). */
  seatsLeft: number | null;
  /** When the credit period `creditsUsed` counts from began; null = the trial's one
   * lifetime pot, or a server older than A6. */
  creditsSince: string | null;
};

export const membersResponseSchema = z.object({
  members: z.array(
    z.object({
      userId: z.string(),
      email: z.string(),
      name: z.string().nullable(),
      role,
      status: z.enum(["active", "disabled"]),
      lastSeenAt: z.string().nullable(),
      joinedAt: z.string(),
      // Defaults keep a new panel working against an API that predates A6 (deploy skew).
      creditsUsed: z.number().int().nullable().default(null),
    }),
  ),
  invites: z.array(
    z.object({
      id: z.string(),
      email: z.string(),
      role,
      invitedAt: z.string(),
      expiresAt: z.string(),
      expired: z.boolean(),
    }),
  ),
  inviteBlockedReason: z.string().nullable(),
  seatsLeft: z.number().int().nullable(),
  creditsSince: z.string().nullable().default(null),
});

type InviteRequest = { emails: string[]; role: FirmRole };

export const inviteRequestSchema = z.object({
  emails: z.array(z.string()).min(1).max(50),
  role,
});

/** Named refusals for one invited email (spec §5.3). */
export type InviteRefusal = "invalid_email" | "email_has_account" | "seat_limit" | "invite_pending" | "upstream";

export type InviteOutcome =
  | { email: string; ok: true }
  | { email: string; ok: false; code: InviteRefusal; error: string };

export const inviteResponseSchema = z.object({
  results: z.array(
    z.union([
      z.object({ email: z.string(), ok: z.literal(true) }),
      z.object({
        email: z.string(),
        ok: z.literal(false),
        code: z.enum(["invalid_email", "email_has_account", "seat_limit", "invite_pending", "upstream"]),
        error: z.string(),
      }),
    ]),
  ),
});

type InviteResponse = { results: InviteOutcome[] };

type RoleChangeRequest = { role: FirmRole };

export const roleChangeRequestSchema = z.object({ role });

expectParity<z.infer<typeof membersResponseSchema>, MembersResponse>(true);
expectParity<z.infer<typeof inviteRequestSchema>, InviteRequest>(true);
expectParity<z.infer<typeof inviteResponseSchema>, InviteResponse>(true);
expectParity<z.infer<typeof roleChangeRequestSchema>, RoleChangeRequest>(true);
